import * as DataGenerator from 'data-generator-retail'

// from Payload: src/payload-types.ts
/**
 * This interface was referenced by `Config`'s JSON-Schema
 * via the `definition` "users".
 */
export interface User {
  id: number
  name?: string | null
  role: 'admin' | 'vet' | 'user'
  vets?: (number | User)[] | null
  phone?: string | null
  website?: string | null
  address?: string | null
  zip?: string | null
  clinic_name?: string | null
  banner?: string | null
  profile?: string | null
  settings?:
    | {
        [k: string]: unknown
      }
    | unknown[]
    | string
    | number
    | boolean
    | null
  updatedAt: string
  createdAt: string
  email: string
  /** Lifecycle state. 'invited'/'pending_trial_verification' = not yet
      confirmed (Pending); 'active' = confirmed. Legacy owners may be null. */
  accountStatus?: 'pending_trial_verification' | 'invited' | 'active' | 'disabled' | null
  /** Clinic Team Master/Sub: the master this sub-account belongs to. Null =
      this user IS a master (or a standalone vet). */
  masterAccount?: number | User | null
  /** Clinic Team Master/Sub: a sub-account's lifecycle (null for masters). */
  subAccountStatus?: 'active' | 'pending_invitation' | 'disabled' | 'removed' | null
  /** Professional title shown in the Clinic Team list (e.g. "DVM"). */
  professionalTitle?: string | null
  /** Whether the user receives glucose alert emails. */
  emailAlertsEnabled?: boolean | null
  /** ISO timestamp the invite/claim email was sent (paired with accountStatus) */
  invitationSentAt?: string | null
  resetPasswordToken?: string | null
  resetPasswordExpiration?: string | null
  salt?: string | null
  hash?: string | null
  _verified?: boolean | null
  _verificationToken?: string | null
  loginAttempts?: number | null
  lockUntil?: string | null
  password?: string | null
}

/**
 * This interface was referenced by `Config`'s JSON-Schema
 * via the `definition` "pets".
 */
export interface Pet {
  id: number
  name?: string | null
  dob?: string | null
  photo?: string | null
  gender?: string | null
  owner: number
  species?: ('dogs' | 'cats' | 'horses' | 'cows' | 'rabbits' | 'guinea_pigs' | 'ferrets') | null
  breed?: string | null
  height?: string | null
  weight?: string | null
  color?: string | null
  glucoserange?: string | null

  // Chronic-care profile. Mirrored from ada-core src/payload-types.ts; these
  // are on the dataProvider's pets allowlist and are written through PetEdit +
  // AutoSave. A field missing HERE still round-trips at runtime, but every call
  // site needs an `as any` to reach it — which is how `assignedVet` below was
  // being read before this was filled in.
  /** Numeric weight in kg — drives RER (70 × kg^0.75); preferred over `weight`. */
  weight_kg?: number | null
  /** Spayed/neutered. Drives the DER activity factor. null = unknown. */
  neutered?: boolean | null
  /** For senior-pet DER adjustment (>7y dog / >10y cat). */
  birth_date?: string | null
  /** Vet-assessed Body Condition Score, 1=emaciated / 5=ideal / 9=obese. */
  body_condition_score?: number | null
  /** Vet-managed condition flags; owners see read-only. Shape is open in Payload. */
  chronic_conditions?: { [k: string]: unknown } | unknown[] | string | number | boolean | null
  /** TIR lower bound for this pet, **mg/dL** (Payload default 70). */
  target_glucose_low?: number | null
  /** TIR upper bound for this pet, **mg/dL** (Payload default 180). */
  target_glucose_high?: number | null
  /** Vet-set daily kcal-in target overriding the computed DER. */
  target_kcal_in_override?: number | null
  /**
   * Vet toggle: hide numeric glucose from the owner app (qualitative status +
   * trend only). Owners still read the flag.
   */
  hide_glucose_values?: boolean | null
  /**
   * Clinic Team: the veterinarian RESPONSIBLE for this patient
   * (`pets.assigned_vet_id`). Set only via POST /api/pets/:id/assign-vet.
   * Distinct from `owner.vets`, which is the access substrate — assignment
   * grants no visibility of its own (PRD-MASTER-SUB-ACCOUNT §11.7).
   * Populated to a User at depth >= 1, a bare id otherwise.
   */
  assignedVet?: number | User | null
  /**
   * Every assignee, ascending; `[]` = unassigned (vic/vetpo#70). The source of
   * truth for who is responsible — `assignedVet` above is only a legacy mirror
   * of the first assignee and can hold ANOTHER clinic's vet on a shared pet.
   */
  assignedVetIds?: number[]
  assignedAt?: string | null
  updatedAt: string
  createdAt: string
}

/**
 * Every species value the backend can store on `Pet['species']` — the ONE
 * source PetEdit's species dropdown reads from, so the dropdown can never be
 * narrower than the data (vic/vetpo#8: it used to offer only dogs/cats, so an
 * existing horse/cow/rabbit/guinea_pig/ferret patient opened with no matching
 * selection — reads as blank, invites a vet to "fill it in", and one click
 * mislabels the patient).
 *
 * Declared as a Record OVER the union rather than an array literal so the
 * COMPILER enforces completeness: add a member to `Pet['species']` and this
 * object fails to compile until it is listed here. An array typed
 * `NonNullable<Pet['species']>[]` checks that each entry is valid but NOT that
 * every value is present, so the union could grow and the dropdown would go
 * silently narrow again — which is the whole bug (verified: with the array
 * form, growing the union left tsc, this file's own test, and the species-band
 * pin all green).
 */
const PET_SPECIES_SET: Record<NonNullable<Pet['species']>, true> = {
  dogs: true,
  cats: true,
  horses: true,
  cows: true,
  rabbits: true,
  guinea_pigs: true,
  ferrets: true,
}

export const PET_SPECIES_VALUES = Object.keys(PET_SPECIES_SET) as NonNullable<Pet['species']>[]

/**
 * This interface was referenced by `Config`'s JSON-Schema
 * via the `definition` "devices".
 */
export interface Device {
  id: number
  name: string
  pet: number | Pet
  type?: string | null
  password: string
  activatedAt?: string | null
  start_time?: number | null
  timezone?: number | null
  calibration?: string | null
  platform?: string | null
  model?: string | null
  version?: string | null
  notes?: string | null
  updatedAt: string
  createdAt: string
}
/**
 * This interface was referenced by `Config`'s JSON-Schema
 * via the `definition` "metrics".
 */
export interface Metric {
  id: number
  device: number | Device
  offset: number
  timestamp: number
  glucose: number
  current?: number | null
  quality?: number | null
  status?: number | null
  cv?: number | null
  timezone?: number | null
  trend?: string | null
  srcid?: number | null
  notes?:
    | {
        [k: string]: unknown
      }
    | unknown[]
    | string
    | number
    | boolean
    | null
  updatedAt: string
  createdAt: string
}
/**
 * This interface was referenced by `Config`'s JSON-Schema
 * via the `definition` "emrs".
 */
export interface Emr {
  id: number
  pet: number
  timestamp: string
  notes: string
  includeInReport?: boolean
  // vet: number ; let server populate the value
  additionalInfo?:
    | {
        [k: string]: unknown
      }
    | unknown[]
    | string
    | number
    | boolean
    | null
  updatedAt: string
  createdAt: string
}
/**
 * This interface was referenced by `Config`'s JSON-Schema
 * via the `definition` "treatment-plans" (ada-core vic/ada-core#192).
 * One overwritable "current plan" row per pet, written by
 * POST /api/treatment-plans/pet/:petId/save. insulinConcentration /
 * insulinSyringeType are server-derived from insulinProduct — never send
 * them in a save payload, the server ignores them anyway.
 */
export interface TreatmentPlan {
  id: number
  pet: number
  vet: number
  insulinProduct?: 'vetsulin' | 'u100_generic' | null
  insulinConcentration?: string | null
  insulinSyringeType?: string | null
  insulinDoseUnits?: number | null
  insulinTimes?: { time: string; id?: string | null }[] | null
  mealTimes?: { time: string; id?: string | null }[] | null
  eatingLessInstructions?: string | null
  notEatingInstructions?: string | null
  mealReminderEnabled?: boolean | null
  homeInstructions?: string | null
  updatedAt: string
  createdAt: string
}
/**
 * Structured meal-logging event from ada-core's FeedingEvents collection
 * (vic/ada-core#206). `outcome` is owner-set per FOOD meal only and is
 * absent/null when the meal was not logged — never infer "normal" from a
 * missing value. See vic/vetpo#15.
 */
export interface FeedingEvent {
  id: number
  pet: number | Pet
  kind: 'food' | 'water' | 'treat'
  outcome?: 'normal' | 'less' | 'none' | null
  scheduledTime?: string | null
  actualTime?: string | null
  updatedAt: string
  createdAt: string
}
/**
 * Structured insulin-administration event from ada-core's InsulinEvents
 * collection. Replaces the legacy `activities.description` JSON blob as the
 * source for insulin markers going forward (vic/vetpo#15) — historical
 * legacy-format `activities` rows are unaffected and keep rendering as
 * before. `guidance` (ada-core#193) records which prompt the owner was shown:
 * 'default' is the routine dose prompt; the other three are exception
 * instructions. NULL means none was recorded.
 */
export interface InsulinEvent {
  id: number
  pet: number | Pet
  owner: number | User
  eventType: 'dose' | 'skipped' | 'pen_low' | 'pen_empty'
  insulinType: string
  units: number
  scheduledTime?: string | null
  actualTime: string
  guidance?: 'default' | 'eating_less' | 'not_eating' | 'contact_clinic' | null
  /**
   * Owner-set void (ada-core#298): the owner may void their own
   * `source: 'manual'` dose within 24h of logging it instead of editing it —
   * nothing here is ever edited or deleted. NULL/absent = active. Once set,
   * permanent. A voided row must be excluded from dose markers/totals, not
   * treated as Logged.
   */
  voidedAt?: string | null
  voidReason?: string | null
  updatedAt: string
  createdAt: string
}
/**
 * This interface was referenced by `Config`'s JSON-Schema
 * via the `definition` "activities".
 */
export interface Activity {
  id: number
  owner: number | User
  pet?: number | Pet
  type: string
  start_time?: number | null // Unix timestamp in seconds
  photo?: string | null
  time?: number | null
  end_time?: number | null // Unix timestamp in seconds
  description?: string | null
  srcid?: number | null
  created_at?: string | null
  updated_at?: string | null
  updatedAt: string
  createdAt: string
}
/**
 * This interface was referenced by `Config`'s JSON-Schema
 * via the `definition` "xlogs".
 */
export interface Xlog {
  id: number
  owner: number | User
  timestamp: string
  type: string
  value: string
  srcid?: number | null
  updatedAt: string
  createdAt: string
}

export type ThemeName = 'light' | 'dark'

export type Category = DataGenerator.Category
export type Product = DataGenerator.Product
export type Customer = DataGenerator.Customer
export type Order = DataGenerator.Order
export type Invoice = DataGenerator.Invoice
export type Review = DataGenerator.Review
export type BasketItem = DataGenerator.BasketItem

declare global {
  interface Window {
    restServer: any
  }
}
